import { isNil, isEmpty, sortBy } from 'lodash';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import classNames from 'classnames';
import Spinner from 'react16-spinjs';
import TRCAudioContainer from '../../audio/TRCAudioContainer';
import StepProgress from '../../../common/components/StepProgress';
import MiniCardLayout from '../../../common/layouts/miniCardLayout';
import MicTestCardContainer from './InstructionCards/MicTestCardContainer';
import MicHelpPopup from '../../../common/components/helpPopup/MicHelpPopup';
import MicPermissionsCardContainer from './InstructionCards/MicPermissionsCardContainer';
import ReadingInstructionCardContainer from './InstructionCards/ReadingInstructionCardContainer';
import {
    getAudioStream,
    isMicPermissionDenied,
    isMicPermissionGranted,
    getMicPermissionStatus,
    isDemoVideoAlreadyWatched,
    setWatchedDemoVideoFlagToLocalStorage,
} from './TROInstructionsUtils';
import {
    getStepsCount,
    getFinalCurrentStepIndex,
    setArPageTitle,
    redirectToARwithError,
} from '../../troUtils';
import GoButtonDark from '../../../common/components/GoButtonDark';
import {
    INSTRUCTIONS_CARDS_IDS,
    CARDS_CLASSNAMES,
    INSTRUCTIONS_CARDS_ORDER,
    NEXT_CARD_INCREMENT,
    SKIP_NEXT_INCREMENT,
} from './TROInstructionsConstants';
import { SPINNER } from '../../../constants/components';
import RecordingVisualizer from '../../../common/recording/components/RecordingVisualizer';
import MultiChoiceReadingCheckContainer from '../readingCheck/MultiChoiceReadingCheckContainer';
import MicReadingCheckContainer from '../readingCheck/MicReadingCheckContainer';
import { PRE_ASSESSMENT_STEPS } from '../../troConstants';
import { isIPadSafari } from '../../../common/utils/browserUtils';
import { ARIA_LABELS } from '../../../constants/text';

class TROInstructionsView extends Component {
    constructor(props) {
        super(props);

        setArPageTitle(props.isExternalURLSourcePresent, 'Microphone Test');

        this.state = {
            micInitialStatus: null,
            helpPopupProblemType: null,
            goButtonEnabled: false,
            cards: [],
        };

        this.permissionStatus = null;
        this.isVideoWatchedInPreviousSession = isDemoVideoAlreadyWatched(
            this.props.userId,
            this.props.grade,
            this.props.period,
        );

        this.setNextWithTracking = section => {
            if (!this.props.isExternalURLSourcePresent) {
                this.props.trackNextPreAssesmentStep(section);
            }
            this.setNextCard();
        };

        this.skipNextWithTracking = section => {
            if (!this.props.isExternalURLSourcePresent) {
                this.props.trackNextPreAssesmentStep(section);
            }
            this.skipNextCard();
        };

        this.readingInstructionCard = () => {
            const { goButtonEnabled } = this.state;
            const {
                isExternalURLSourcePresent,
                trackStartPreAssessmentStep,
                trackNextPreAssesmentStep,
            } = this.props;
            const { completeCard, isVideoWatchedInPreviousSession, handleGoToNextPage } = this;

            const onSuccess = () => {
                completeCard();
                this.setState({ goButtonEnabled: true });
            };

            const trackStartStep = () => {
                if (!isExternalURLSourcePresent)
                    trackStartPreAssessmentStep(PRE_ASSESSMENT_STEPS.DEMO_VIDEO);
            };

            if (isExternalURLSourcePresent) {
                return handleGoToNextPage();
            }

            const handleClickGoButton = () => {
                if (!isExternalURLSourcePresent) {
                    trackNextPreAssesmentStep(PRE_ASSESSMENT_STEPS.DEMO_VIDEO);
                }
                handleGoToNextPage();
            };

            return (
                <div className="instructions-view-reading-card-wrapper">
                    <div className="reading-card-title">Watch and learn!</div>
                    <ReadingInstructionCardContainer
                        trackStartStep={isExternalURLSourcePresent ? undefined : trackStartStep}
                        onSuccess={onSuccess}
                        isVideoWatchedInPreviousSession={isVideoWatchedInPreviousSession}
                    />
                    <GoButtonDark
                        onClick={handleClickGoButton}
                        isEnabled={goButtonEnabled}
                        isPulsating={goButtonEnabled}
                        ariaLabel={ARIA_LABELS.GO_TO_NEXT_STEP}
                    />
                    <div className={classNames('button-tip', { show: goButtonEnabled })}>
                        I got this,
                        <br />
                        ready to start!
                    </div>
                </div>
            );
        };

        this.micTestCard = () => {
            const trackStartPreAssessmentStep = () =>
                this.props.trackStartPreAssessmentStep(PRE_ASSESSMENT_STEPS.MIC_CHECK);
            return (
                <MicTestCardContainer
                    onSuccess={this.completeCard}
                    onFailure={this.handleOnFailure}
                    onDone={() => this.setNextWithTracking(PRE_ASSESSMENT_STEPS.MIC_CHECK)}
                    trackStartPreAssessmentStep={trackStartPreAssessmentStep}
                />
            );
        };

        this.micReadingCheckCard = () => {
            const trackStartPreAssessmentStep = () =>
                this.props.trackStartPreAssessmentStep(PRE_ASSESSMENT_STEPS.READING_CHECK);
            return (
                <MicReadingCheckContainer
                    onSuccess={this.completeCard}
                    onFailure={this.setNextCard}
                    onDone={() => this.skipNextWithTracking(PRE_ASSESSMENT_STEPS.READING_CHECK)}
                    trackStartPreAssessmentStep={trackStartPreAssessmentStep}
                />
            );
        };

        this.multiChoiceReadingCheckCard = () => (
            <MultiChoiceReadingCheckContainer
                onSuccess={this.completeCard}
                onFailure={this.props.goToOwlyInterstitial}
                onDone={() => this.setNextWithTracking(PRE_ASSESSMENT_STEPS.READING_CHECK)}
            />
        );

        this.micPermissionsCard = {
            id: INSTRUCTIONS_CARDS_IDS.MIC_PERMISSION_CARD,
            order: INSTRUCTIONS_CARDS_ORDER[INSTRUCTIONS_CARDS_IDS.MIC_PERMISSION_CARD],
            component: () => (
                <MicPermissionsCardContainer
                    onDone={this.setNextCard}
                    onSuccess={this.completeCard}
                    onFailure={this.handleOnFailure}
                    micInitialStatus={this.state.micInitialStatus}
                />
            ),
        };

        this.hasVisualizerIds = [
            INSTRUCTIONS_CARDS_IDS.MIC_TEST_CARD,
            INSTRUCTIONS_CARDS_IDS.MIC_READING_CHECK_CARD,
        ];

        this.testCardIds = [
            INSTRUCTIONS_CARDS_IDS.MIC_TEST_CARD,
            INSTRUCTIONS_CARDS_IDS.MIC_READING_CHECK_CARD,
            INSTRUCTIONS_CARDS_IDS.MULTI_CHOICE_READING_CHECK_CARD,
        ];
    }

    async componentDidMount() {
        const {
            isExternalURLSourcePresent,
            setPreviouslyEnabledMic,
            setProgressStepsCount,
            setRecordingStream,
            onPermissionsError,
            startPreAssessmentTimeTracking,
        } = this.props;
        const permissionStatus = await getMicPermissionStatus();

        if (isNil(permissionStatus)) {
            setProgressStepsCount(getStepsCount(isExternalURLSourcePresent));
            this.handleSetCards([this.micPermissionsCard]);
            return;
        }

        this.setMicInitialStatus(permissionStatus.state);
        this.permissionStatus = permissionStatus;
        this.permissionStatus.addEventListener('change', this.onPermissionsStatusChange);

        const isGranted = isMicPermissionGranted(permissionStatus.state);

        setProgressStepsCount(getStepsCount(isExternalURLSourcePresent, isGranted));

        if (!isExternalURLSourcePresent) startPreAssessmentTimeTracking();

        if (isGranted) {
            this.handleSetCards();
            if (!isExternalURLSourcePresent) setPreviouslyEnabledMic();

            try {
                const stream = await getAudioStream();
                setRecordingStream(stream);
            } catch ({ name }) {
                onPermissionsError(name);
            }
        } else {
            this.handleSetCards([this.micPermissionsCard]);
        }
    }

    componentDidUpdate(prevProps, prevState) {
        const { areAllDemoVideosWatched, userId, grade, period } = prevProps;
        if (
            areAllDemoVideosWatched &&
            areAllDemoVideosWatched !== prevState.areAllDemoVideosWatched
        ) {
            setWatchedDemoVideoFlagToLocalStorage(userId, grade, period);
        }
    }

    componentWillUnmount() {
        this.props.hideHelpPopup();

        if (!isNil(this.permissionStatus)) {
            this.permissionStatus.removeEventListener('change', this.onPermissionsStatusChange);
        }
    }

    handleSetCards = (extraCards = []) => {
        this.setState({
            cards: sortBy(
                [
                    {
                        id: INSTRUCTIONS_CARDS_IDS.DEMO_VIDEO_CARD,
                        order: INSTRUCTIONS_CARDS_ORDER[INSTRUCTIONS_CARDS_IDS.DEMO_VIDEO_CARD],
                        component: this.readingInstructionCard,
                    },
                    {
                        id: INSTRUCTIONS_CARDS_IDS.MIC_TEST_CARD,
                        order: INSTRUCTIONS_CARDS_ORDER[INSTRUCTIONS_CARDS_IDS.MIC_TEST_CARD],
                        component: this.micTestCard,
                    },
                    {
                        id: INSTRUCTIONS_CARDS_IDS.MIC_READING_CHECK_CARD,
                        order:
                            INSTRUCTIONS_CARDS_ORDER[INSTRUCTIONS_CARDS_IDS.MIC_READING_CHECK_CARD],
                        component: this.micReadingCheckCard,
                    },
                    {
                        id: INSTRUCTIONS_CARDS_IDS.MULTI_CHOICE_READING_CHECK_CARD,
                        order:
                            INSTRUCTIONS_CARDS_ORDER[
                                INSTRUCTIONS_CARDS_IDS.MULTI_CHOICE_READING_CHECK_CARD
                            ],
                        component: this.multiChoiceReadingCheckCard,
                    },
                    ...extraCards,
                ],
                'order',
            ),
        });
    };

    onPermissionsStatusChange = ({ target }) => {
        this.setMicInitialStatus(target.state);

        if (this.props.didPermissionsFail && !isMicPermissionDenied(target.state)) {
            this.props.clearPermissionsError();
        }
    };

    setMicInitialStatus = status => this.setState({ micInitialStatus: status });

    handleOnFailure = helpPopupProblemType => {
        this.setState({ helpPopupProblemType });
        this.props.showHelpPopup();
    };

    handleGoToNextPage = () => {
        if (
            isIPadSafari() ||
            isNil(this.state.micInitialStatus) ||
            isMicPermissionGranted(this.state.micInitialStatus)
        ) {
            this.props.goToNextPage();
        }
    };

    setNextCard = () => {
        const { currentStepIndex } = this.props.onboardingProgressBar;
        this.props.setCurrentStepIndex(currentStepIndex + NEXT_CARD_INCREMENT);
    };

    skipNextCard = () => {
        const { currentStepIndex } = this.props.onboardingProgressBar;
        this.props.setCurrentStepIndex(currentStepIndex + SKIP_NEXT_INCREMENT);
    };

    completeCard = () => {
        const { completedStepIndex } = this.props.onboardingProgressBar;
        this.props.setCompletedStepIndex(completedStepIndex + NEXT_CARD_INCREMENT);
    };

    getIsMicCardActive = () => {
        const { cards } = this.state;
        const { onboardingProgressBar } = this.props;
        const { currentStepIndex } = onboardingProgressBar;
        const finalCurrentStepIndex = getFinalCurrentStepIndex(currentStepIndex, cards);
        const currentId = cards[finalCurrentStepIndex] ? cards[finalCurrentStepIndex].id : null;
        const needsVisualizer = this.hasVisualizerIds.includes(currentId);
        const isCardsNotEmpty = !isEmpty(this.state.cards);
        return needsVisualizer && isCardsNotEmpty;
    };

    getHasOverlay = () => {
        const { cards } = this.state;
        const { onboardingProgressBar } = this.props;
        const { currentStepIndex } = onboardingProgressBar;
        const finalCurrentStepIndex = getFinalCurrentStepIndex(currentStepIndex, cards);
        const currentId = cards[finalCurrentStepIndex] ? cards[finalCurrentStepIndex].id : null;
        const needsVisualizer = this.testCardIds.includes(currentId);
        const isCardsNotEmpty = !isEmpty(this.state.cards);
        return needsVisualizer && isCardsNotEmpty;
    };

    handleArMicTestFailure = () => {
        redirectToARwithError(this.props.isBenchmarkModeEnabled);
    };

    render() {
        const { helpPopupProblemType, cards } = this.state;
        const {
            isScreenBlocked,
            isHelpPopupVisible,
            goToStart,
            onboardingProgressBar,
            onMicTestRetry,
            logUserOut,
            isRecordingInProgress,
            isExternalURLSourcePresent,
            isBenchmarkModeEnabled,
            trackTroError,
            trackFailedEnableMic,
        } = this.props;
        const { currentStepIndex, completedStepIndex, stepsCount } = onboardingProgressBar;

        const finalCurrentStepIndex = getFinalCurrentStepIndex(currentStepIndex, cards);
        const isMicCardActive = this.getIsMicCardActive();
        const hasOverlay = this.getHasOverlay();
        const circlesToFill = completedStepIndex + 1;
        const onRestart = () => goToStart(isExternalURLSourcePresent);

        return (
            <div
                className={classNames(
                    'trc-instructions',
                    !isEmpty(cards) && CARDS_CLASSNAMES[cards[finalCurrentStepIndex].id],
                )}
            >
                {isMicCardActive && (
                    <RecordingVisualizer
                        hasRecordingStatusBox
                        isVisualizerActive={isRecordingInProgress}
                        isExternalURLSourcePresent={isExternalURLSourcePresent}
                    />
                )}
                {isHelpPopupVisible && (
                    <MicHelpPopup
                        onRestart={onRestart}
                        problem={helpPopupProblemType}
                        onTestRetry={onMicTestRetry}
                        onLogout={logUserOut}
                        onArMicTestFailure={this.handleArMicTestFailure}
                        isActionButtonDisabled={isExternalURLSourcePresent && isScreenBlocked}
                        isExternalURLSourcePresent={isExternalURLSourcePresent}
                        isBenchmarkModeEnabled={isBenchmarkModeEnabled}
                        trackTroError={trackTroError}
                        trackFailedEnableMic={trackFailedEnableMic}
                    />
                )}
                <MiniCardLayout
                    isScreenBlocked={isScreenBlocked}
                    className={classNames(hasOverlay && 'mic-test')}
                >
                    {isEmpty(cards) ? (
                        <Spinner config={SPINNER.DARK_SMALL} />
                    ) : (
                        cards[finalCurrentStepIndex].component()
                    )}
                    <TRCAudioContainer />
                    <StepProgress
                        stepsCount={stepsCount}
                        currentStepIndex={circlesToFill}
                        completedStepIndex={completedStepIndex}
                    />
                </MiniCardLayout>
            </div>
        );
    }
}

TROInstructionsView.propTypes = {
    period: PropTypes.string.isRequired,
    grade: PropTypes.string.isRequired,
    userId: PropTypes.string.isRequired,
    isScreenBlocked: PropTypes.bool,
    didPermissionsFail: PropTypes.bool,
    isHelpPopupVisible: PropTypes.bool,
    areAllDemoVideosWatched: PropTypes.bool,
    onboardingProgressBar: PropTypes.object,
    setCurrentStepIndex: PropTypes.func,
    setCompletedStepIndex: PropTypes.func,
    goToNextPage: PropTypes.func,
    goToStart: PropTypes.func,
    showHelpPopup: PropTypes.func,
    hideHelpPopup: PropTypes.func,
    onPermissionsError: PropTypes.func,
    setRecordingStream: PropTypes.func,
    clearPermissionsError: PropTypes.func,
    onMicTestRetry: PropTypes.func,
    logUserOut: PropTypes.func,
    setProgressStepsCount: PropTypes.func,
    isRecordingInProgress: PropTypes.bool,
    goToOwlyInterstitial: PropTypes.func,
    isExternalURLSourcePresent: PropTypes.bool.isRequired,
    isBenchmarkModeEnabled: PropTypes.bool.isRequired,
    trackTroError: PropTypes.func,
    trackFailedEnableMic: PropTypes.func,
    trackStartPreAssessmentStep: PropTypes.string.isRequired,
    trackNextPreAssesmentStep: PropTypes.func.isRequired,
    setPreviouslyEnabledMic: PropTypes.func.isRequired,
    startPreAssessmentTimeTracking: PropTypes.func.isRequired,
};

TROInstructionsView.defaultProps = {
    isScreenBlocked: false,
    didPermissionsFail: false,
    isHelpPopupVisible: false,
    areAllDemoVideosWatched: false,
    onboardingProgressBar: {},
    setCurrentStepIndex: () => {},
    setProgressStepsCount: () => {},
    setCompletedStepIndex: () => {},
    goToNextPage: () => {},
    goToStart: () => {},
    showHelpPopup: () => {},
    hideHelpPopup: () => {},
    onPermissionsError: () => {},
    setRecordingStream: () => {},
    clearPermissionsError: () => {},
    onMicTestRetry: () => {},
    logUserOut: () => {},
    isRecordingInProgress: false,
    goToOwlyInterstitial: () => {},
    trackTroError: () => {},
    trackFailedEnableMic: () => {},
};

export default TROInstructionsView;
